import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { HorizonTrackingService } from '@services/api/horizon-tracking.service';
import { shouldHideError } from '@services/helpers/server-error-silence';
import { ToasterService } from '@services/util/toaster.service';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { apiErrorBody } from '@services/api/api-error';
import moment from 'moment';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { HorizonShare, HorizonSharesPage } from '../lead-horizon-tracking.model';
import { HorizonShareDialogComponent } from './horizon-share-dialog/horizon-share-dialog.component';

/** Brisbane has no daylight saving, so shared-at times are shown at a fixed +10:00. */
const BRISBANE_UTC_OFFSET_MINUTES = 600;
const PREVIEW_COUNT = 3;
const PAGE_SIZE = 10;

/**
 * Shared With Buyer on the lead's Horizon tab.
 *
 * Lots, floor plans and documents an agent has shared with this buyer, when, and whether the buyer
 * has opened them. "Share New Items" sends more: lots from the project, and only the documents an
 * admin approved in the Sharing Library. The buyer is notified in Horizon when something is shared.
 */
@Component({
  selector: 'app-horizon-shared-with-buyer',
  standalone: true,
  imports: [CommonModule, TooltipModule, HorizonShareDialogComponent],
  templateUrl: './horizon-shared-with-buyer.component.html',
  styleUrls: ['../horizon-card.scss', './horizon-shared-with-buyer.component.scss'],
})
export class HorizonSharedWithBuyerComponent implements OnChanges, OnDestroy {
  @Input() leadId: string | null = null;
  @Input() projectId: string | null = null;
  /** Sharing needs a Horizon account the buyer can sign in to (invited or active). */
  @Input() canShare = false;

  summary = { properties: 0, documents: 0 };
  shares: HorizonShare[] = [];
  hasMore = false;
  showAll = false;
  isLoading = false;
  isLoadingMore = false;
  hasLoadError = false;
  isRevokeBusy = false;

  /** The "Share New Items" dialog. */
  isDialogOpen = false;

  private page = 1;
  private subscription: Subscription;

  constructor(
    private horizon: HorizonTrackingService,
    private toaster: ToasterService,
    private router: Router,
    private remmiConfirm: RemmiConfirmService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId'] && this.leadId) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  // ---- Loading ---------------------------------------------------------------------------

  load(): void {
    this.subscription?.unsubscribe();
    this.page = 1;
    this.shares = [];
    this.isLoading = true;
    this.hasLoadError = false;
    this.fetch();
  }

  private fetch(): void {
    const pageSize = this.showAll ? PAGE_SIZE : PREVIEW_COUNT;
    this.subscription = this.horizon.getLeadShares(this.leadId, this.page, pageSize).subscribe({
      next: res => {
        const data: HorizonSharesPage = res?.data;
        this.summary = data?.summary ?? { properties: 0, documents: 0 };
        this.shares = [...this.shares, ...(data?.items ?? [])];
        // The preview shows only the newest few; View All keeps paging.
        this.hasMore = this.showAll ? !!data?.has_more : (data?.total ?? 0) > this.shares.length;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: () => {
        this.hasLoadError = !shouldHideError(null);
        this.isLoading = false;
        this.isLoadingMore = false;
      },
    });
  }

  toggleViewAll(): void {
    this.showAll = !this.showAll;
    this.load();
  }

  loadMore(): void {
    this.page++;
    this.isLoadingMore = true;
    this.fetch();
  }

  get isEmpty(): boolean {
    return !this.isLoading && !this.hasLoadError && !this.shares.length;
  }

  get summaryText(): string {
    const { properties, documents } = this.summary;
    if (!properties && !documents) {
      return 'Nothing has been shared with this buyer yet.';
    }
    const parts = [];
    if (properties) {
      parts.push(`${properties} propert${properties === 1 ? 'y' : 'ies'}`);
    }
    if (documents) {
      parts.push(`${documents} document${documents === 1 ? '' : 's'}`);
    }
    return `${parts.join(' and ')} shared with this buyer.`;
  }

  // ---- Share cards -----------------------------------------------------------------------

  formatShared(date: string): string {
    return moment(date).utcOffset(BRISBANE_UTC_OFFSET_MINUTES).format('D MMM YYYY, h:mm A');
  }

  initials(name: string): string {
    const words = (name ?? '').trim().split(/\s+/);
    return ((words[0]?.[0] ?? '') + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
  }

  /** Images that failed to load, as `<share_id>:<field>`, so the card falls back instead of showing a broken picture. */
  private readonly brokenImages = new Set<string>();

  /** The lot's picture: its image, else its floor plan (Remmi lots often have only the plan). */
  lotImage(share: HorizonShare): string | null {
    const lot = share.lot;
    if (!lot || this.brokenImages.has(`${share.share_id}:image`)) {
      return null;
    }
    return lot.image || lot.floorplan_image || null;
  }

  docThumbnail(share: HorizonShare): string | null {
    return this.brokenImages.has(`${share.share_id}:thumbnail`) ? null : share.document?.thumbnail || null;
  }

  /** What the preview opens: the large picture when there is one (a PDF's first page at full size), else the thumbnail. */
  docPreview(share: HorizonShare): string | null {
    return share.document?.image || this.docThumbnail(share);
  }

  markBroken(share: HorizonShare, field: 'image' | 'thumbnail'): void {
    this.brokenImages.add(`${share.share_id}:${field}`);
  }

  /** Opens a picture full size in a new tab. */
  openImage(url: string | null): void {
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  /** Opens the lot in Project → Price List in a new browser tab, so the agent stays on the lead's Horizon tab. */
  openLot(share: HorizonShare): void {
    if (share.lot?.unit_id && this.projectId) {
      const tree = this.router.createUrlTree(['/projects', 'edit_', this.projectId, 'price-list'], { queryParams: { id: share.lot.unit_id } });
      window.open(this.router.serializeUrl(tree), '_blank', 'noopener');
    }
  }

  askRevoke(share: HorizonShare): void {
    this.remmiConfirm
      .ask({
        title: 'Revoke Access?',
        message: 'The buyer will no longer be able to open this shared item. You can share it with them again later.',
        subject: share.lot?.name || share.document?.name,
        confirmLabel: 'Revoke Access',
        tone: 'danger',
      })
      .subscribe(isConfirmed => isConfirmed && this.runRevoke(share));
  }

  runRevoke(share: HorizonShare): void {
    if (this.isRevokeBusy) {
      return;
    }
    this.isRevokeBusy = true;
    this.horizon.deleteLeadShare(this.leadId, share.share_id).subscribe({
      next: res => {
        this.isRevokeBusy = false;
        this.toaster.Success(
          /^(ok|success)\.?$/i.test(res?.message ?? '') || !res?.message ? 'Access revoked. The buyer can no longer open this item.' : res.message
        );
        this.load();
      },
      error: err => {
        this.isRevokeBusy = false;
        this.toaster.Error(apiErrorBody(err).message || 'Access could not be revoked.');
      },
    });
  }

  // ---- Share New Items -----------------------------------------------------------------------

  openDialog(): void {
    this.isDialogOpen = true;
  }

  trackByShare(_index: number, share: HorizonShare): string {
    return share.share_id;
  }
}
